/**
 * Ticket Statistics
 * Admin dashboard content for ticket metrics — summary counts, average resolution time, and
 * breakdowns by status/priority/type.
 *
 * Lives as its own component (rather than a page) because it is now the "statistics" tab of
 * ModeratorTicketsPage; AdminTicketStatisticsPage just redirects there for old links.
 */

import { useEffect, useState } from 'react'
import { Box, Button, Paper, Grid, CircularProgress, Alert, Card, CardContent, Divider, Typography } from '@mui/material'
import { ConfirmationNumber, CheckCircle, HourglassBottom, Warning, TrendingUp } from '@mui/icons-material'
import { getTicketStatistics } from '@api/services/tickets.api'
import {
  TicketStatisticsDto,
  TicketStatus,
  TicketPriority,
  TicketType,
  TicketStatusLabels,
  TicketPriorityLabels,
  TicketTypeLabels,
} from '@api/types/tickets'

function formatTime(minutes: number) {
  if (minutes < 60) return `${Math.round(minutes)} minutes`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return `${hours}h ${mins}m`
}

/**
 * A breakdown key back to its enum value.
 *
 * The server serialises these dictionaries keyed by the enum's NAME ("Pending"), because that is
 * what System.Text.Json does with an enum-keyed dictionary — everywhere else on this API enums are
 * ordinals, which is why this was written as parseInt and rendered every label as blank. Both
 * spellings are accepted so a future switch to ordinal keys does not empty the labels again.
 */
function enumKey<E extends Record<string, string | number>>(enumObj: E, key: string): E[keyof E] | undefined {
  if (/^-?\d+$/.test(key)) return Number(key) as E[keyof E]
  const byName = enumObj[key as keyof E]
  return typeof byName === 'number' ? (byName as E[keyof E]) : undefined
}

export function TicketStatistics() {
  const [statistics, setStatistics] = useState<TicketStatisticsDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Bumped by Retry to run the fetch again. */
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getTicketStatistics()
      .then((data) => {
        if (!cancelled) setStatistics(data)
      })
      .catch((err: unknown) => {
        console.error('Failed to load statistics:', err)
        if (!cancelled) setError(err instanceof Error && err.message ? err.message : 'Failed to load statistics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !statistics) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </Button>
        }
      >
        {error || 'Failed to load statistics'}
      </Alert>
    )
  }

  const summaryCards = [
    { label: 'Total Tickets', value: statistics.totalTickets, icon: <ConfirmationNumber color="primary" /> },
    { label: 'Open Tickets', value: statistics.openTickets, icon: <HourglassBottom color="warning" /> },
    { label: 'Closed Tickets', value: statistics.closedTickets, icon: <CheckCircle color="success" /> },
    { label: 'Escalated Tickets', value: statistics.escalatedTickets, icon: <Warning color="error" /> },
  ]

  return (
    <Box>
      {/* Summary Cards — 2-up on a phone, 4-up at md and up */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {card.icon}
                  <Typography variant="h6">{card.value}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Average Resolution Time */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TrendingUp color="primary" />
          <Typography variant="h6">Average Resolution Time</Typography>
        </Box>
        <Typography variant="h4" color="primary.main">
          {formatTime(statistics.averageResolutionTimeMinutes)}
        </Typography>
      </Paper>

      {/* Detailed Breakdowns */}
      <Grid container spacing={2}>
        {/* Status Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              By Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(statistics.ticketsByStatus).map(([status, count]) => {
                const statusEnum = enumKey(TicketStatus, status) as TicketStatus
                return (
                  <Box
                    key={status}
                    sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                      {TicketStatusLabels[statusEnum]}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Priority Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              By Priority
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(statistics.ticketsByPriority).map(([priority, count]) => {
                const priorityEnum = enumKey(TicketPriority, priority) as TicketPriority
                return (
                  <Box
                    key={priority}
                    sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                      {TicketPriorityLabels[priorityEnum]}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Type Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              By Type
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(statistics.ticketsByType).map(([type, count]) => {
                const typeEnum = enumKey(TicketType, type) as TicketType
                return (
                  <Box
                    key={type}
                    sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', minWidth: 0, wordBreak: 'break-word' }}>
                      {TicketTypeLabels[typeEnum]}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {count}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}
